import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import api from '../services/api';

// Check if user is authenticated
const isAuthenticated = (): boolean => {
  const token = localStorage.getItem('token');
  return !!token;
};

interface Snippet {
  id: string;
  snippetId: string;
  websiteUrl: string;
  isVerified: boolean;
  status: string;
}

interface SnippetState {
  snippets: Snippet[];
  hasVerifiedSnippet: boolean;
  isLoading: boolean;
  error: string | null;
}

interface SnippetContextType {
  snippets: Snippet[];
  hasVerifiedSnippet: boolean;
  isLoading: boolean;
  error: string | null;
  fetchSnippets: () => Promise<void>;
  initializeSnippets: () => Promise<void>;
  addSnippet: (websiteUrl: string) => Promise<void>;
  deleteSnippet: (id: string) => Promise<void>;
  verifySnippet: (id: string) => Promise<void>;
}

const SnippetContext = createContext<SnippetContextType | undefined>(undefined);

export const useSnippet = () => {
  const context = useContext(SnippetContext);
  if (!context) {
    throw new Error('useSnippet must be used within a SnippetProvider');
  }
  return context;
};

interface SnippetProviderProps {
  children: ReactNode;
}

export const SnippetProvider: React.FC<SnippetProviderProps> = ({ children }) => {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [hasVerifiedSnippet, setHasVerifiedSnippet] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSnippets = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await api.get('/snippets');
      // Handle the API response structure: { snippets: [...] }
      const fetchedSnippets = response.data.snippets || response.data || [];
      
      setSnippets(fetchedSnippets);
      setHasVerifiedSnippet(fetchedSnippets.some((snippet: Snippet) => snippet.isVerified));
    } catch (err: any) {
      console.error('Error fetching snippets:', err);
      setError(err.response?.data?.message || 'Failed to fetch snippets');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const initializeSnippets = useCallback(async () => {
    if (isAuthenticated()) {
      await fetchSnippets();
    } else {
      setIsLoading(false);
    }
  }, [fetchSnippets]);

  const addSnippet = async (websiteUrl: string) => {
    try {
      setError(null);
      const response = await api.post('/snippets', { websiteUrl });
      const newSnippet = response.data;
      
      setSnippets(prev => [...prev, newSnippet]);
      
      // If this is the first snippet and it's verified, update hasVerifiedSnippet
      if (newSnippet.isVerified && !hasVerifiedSnippet) {
        setHasVerifiedSnippet(true);
      }
    } catch (err: any) {
      console.error('Error adding snippet:', err);
      setError(err.response?.data?.message || 'Failed to add snippet');
      throw err;
    }
  };

  const deleteSnippet = async (id: string) => {
    try {
      setError(null);
      await api.delete(`/snippets/${id}`);
      
      setSnippets(prev => {
        const updatedSnippets = prev.filter(snippet => snippet.id !== id);
        // Check if we still have any verified snippets
        const stillHasVerified = updatedSnippets.some(snippet => snippet.isVerified);
        setHasVerifiedSnippet(stillHasVerified);
        return updatedSnippets;
      });
    } catch (err: any) {
      console.error('Error deleting snippet:', err);
      setError(err.response?.data?.message || 'Failed to delete snippet');
      throw err;
    }
  };

  const verifySnippet = async (id: string) => {
    try {
      setError(null);
      const response = await api.post(`/snippets/${id}/verify`);
      const updatedSnippet = response.data;
      
      setSnippets(prev => 
        prev.map(snippet => 
          snippet.id === id ? updatedSnippet : snippet
        )
      );
      
      if (updatedSnippet.isVerified) {
        setHasVerifiedSnippet(true);
      }
    } catch (err: any) {
      console.error('Error verifying snippet:', err);
      setError(err.response?.data?.message || 'Failed to verify snippet');
      throw err;
    }
  };

  const value: SnippetContextType = {
    snippets,
    hasVerifiedSnippet,
    isLoading,
    error,
    fetchSnippets,
    initializeSnippets,
    addSnippet,
    deleteSnippet,
    verifySnippet
  };

  return (
    <SnippetContext.Provider value={value}>
      {children}
    </SnippetContext.Provider>
  );
};
